<script lang="ts">
  /**
   * The top of a screen one step below a tab (Start a co-op game, Join a
   * game, the month's calendar): a back chevron, the screen's title as its
   * `<h1>`, and room on the right for one control or a mirror spacer, so the
   * title stays centred.
   *
   * `focusOnMount` moves focus to the title when the screen appears, so a
   * keyboard or screen-reader user entering a new screen starts at its name
   * rather than on a control of the screen they left, now gone.
   *
   * Since 1.2.0: `trailing` may be wider than the chevron (a chip, two
   * buttons) and the title stays centred on the screen, shrinking only if
   * it must; `safeArea` pads the top by the notch for a header that is the
   * first thing on a full-screen view. Test ids: `testid` on the header,
   * `backTestid` (default `ui-screen-back`) on the chevron.
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';

  interface Props {
    readonly title: string;
    readonly onback: () => void;
    /** The chevron's name; the shell's own "Back" by default. */
    readonly backLabel?: string | undefined;
    readonly trailing?: Snippet | undefined;
    readonly focusOnMount?: boolean;
    /** Pad the top by `env(safe-area-inset-top)`. */
    readonly safeArea?: boolean;
    readonly testid?: string | undefined;
    readonly backTestid?: string | undefined;
  }
  const {
    title,
    onback,
    backLabel,
    trailing,
    focusOnMount = false,
    safeArea = false,
    testid,
    backTestid = 'ui-screen-back',
  }: Props = $props();

  let heading = $state<HTMLHeadingElement | null>(null);
  $effect(() => {
    if (focusOnMount && heading !== null) heading.focus({ preventScroll: true });
  });
</script>

<header class="ui-screen-header" class:ui-screen-header--safe={safeArea} data-testid={testid}>
  <button
    type="button"
    class="ui-screen-back"
    aria-label={backLabel ?? shellText('back')}
    data-testid={backTestid}
    onclick={onback}
  >
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="M15 5l-7 7 7 7"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>
  <h1 bind:this={heading} class="ui-screen-title" tabindex="-1">{title}</h1>
  <span class="ui-screen-trailing">{@render trailing?.()}</span>
</header>

<style>
  .ui-screen-header {
    flex: 0 0 auto;
    display: grid;
    /* Each side is at least as wide as what it holds (the chevron, or a
       trailing wider than it), so nothing overlaps the title: the title
       shrinks, with an ellipsis, before a side does. With room to spare the
       two sides share it equally and the title sits centred. */
    grid-template-columns: minmax(max-content, 1fr) minmax(0, auto) minmax(max-content, 1fr);
    align-items: center;
    gap: var(--ui-space-2);
    padding: var(--ui-space-2) var(--ui-space-3);
    font-family: var(--ui-font);
  }

  .ui-screen-header--safe {
    padding-top: calc(var(--ui-space-2) + env(safe-area-inset-top));
  }

  .ui-screen-back {
    justify-self: start;
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--ui-accent);
  }

  .ui-screen-back:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-screen-title {
    outline: none;
    margin: 0;
    text-align: center;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-screen-trailing {
    justify-self: end;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--ui-space-2);
    min-width: 0;
  }
</style>
